import { AuxDatePickerDateFormatEnum } from "@blk/aladdin-web-components";
import { AuxFacetedFilterOptionTypeEnum } from "./types";
import { getDateString, tryParseUtcDate } from "common/date-utils";
export const MAX_ITEMS_TO_DISPLAY = 5;
const roundToDecimals = (value, fixedDecimal) => {
    if (value === undefined) {
        return undefined;
    }
    const factor = 10 ** fixedDecimal;
    return Math.round(value * factor) / factor;
};
const getCurrentMultiSelectValues = (fieldId, values) => {
    const currentValues = values[fieldId];
    return currentValues && Array.isArray(currentValues) ? currentValues : [];
};
const getCurrentMultiDateValues = (fieldId, values) => {
    const currentValues = values[fieldId];
    return currentValues && !Array.isArray(currentValues) && currentValues.type === "date" ? currentValues : undefined;
};
const getCurrentMultiNumValues = (fieldId, values) => {
    const currentValues = values[fieldId];
    return currentValues && !Array.isArray(currentValues) && currentValues.type === "number-range" ? currentValues : undefined;
};
const getCurrentPicklistValues = (fieldId, values) => {
    const currentValues = values[fieldId];
    return currentValues && Array.isArray(currentValues) ? currentValues : [];
};
const processDateRangeFilter = (filter, values) => {
    const currentValues = getCurrentMultiDateValues(filter.field, values);
    return {
        endDateValue: currentValues && currentValues.endDate
            ? getDateString(currentValues.endDate, "DD-MM-YYYY")
            : undefined,
        startDateValue: currentValues && currentValues.startDate
            ? getDateString(currentValues.startDate, "DD-MM-YYYY")
            : undefined
    };
};
const processMultiSelectFilter = (filter, values) => {
    const currentValues = getCurrentMultiSelectValues(filter.field, values);
    return filter.values?.map((y) => ({
        checked: currentValues.some((v) => y.codes.includes(v)),
        label: y.label,
        value: y.codes
    }));
};
const processMultiSliderFilter = (filter, values) => {
    const currentValues = getCurrentMultiNumValues(filter.field, values);
    return {
        fixedDecimal: filter.fixedDecimal,
        maxValue: filter.maxValue,
        minValue: filter.minValue,
        rangeStartValue: currentValues?.lowerLimit ?? filter.minValue,
        step: filter.step,
        value: currentValues?.upperLimit ?? filter.maxValue
    };
};
const processPickListFilter = (filter, values, expandedState) => {
    const currentValues = getCurrentPicklistValues(filter.field, values);
    const expandedUids = expandedState?.[filter.field];
    return filter.data?.map((y) => {
        const updatedChildren = y.children
            ? y.children.map((child) => ({
                ...child,
                isChecked: currentValues.includes(`${y.uid};${child.uid}`)
            }))
            : undefined;
        return {
            ...y,
            children: updatedChildren,
            isChecked: currentValues.includes(`${y.uid}`),
            isExpanded: expandedUids ? expandedUids.includes(y.uid) : false
        };
    });
};
const filterProcessors = {
    [AuxFacetedFilterOptionTypeEnum.DateRange]: (filter, values) => processDateRangeFilter(filter, values),
    [AuxFacetedFilterOptionTypeEnum.MultiSelect]: (filter, values) => processMultiSelectFilter(filter, values),
    [AuxFacetedFilterOptionTypeEnum.MultiSlider]: (filter, values) => processMultiSliderFilter(filter, values),
    [AuxFacetedFilterOptionTypeEnum.PickList]: (filter, values, expandedState) => processPickListFilter(filter, values, expandedState)
};
export const getFilterOptionsData = (filter, values, expandedState) => {
    const processor = filterProcessors[filter.type];
    return processor ? processor(filter, values, expandedState) : [];
};
const filterMultiSelectBySearch = (filter, searchValue) => ({
    ...filter,
    values: filter.values.filter((v) => v.label.toLowerCase().includes(searchValue))
});
const filterPicklistBySearch = (filter, searchValue) => {
    const filteredData = filter.data?.map((item) => {
        const itemMatches = item.label?.toLowerCase().includes(searchValue);
        const filteredChildren = item.children
            ? item.children.filter((child) => child.label?.toLowerCase().includes(searchValue))
            : [];
        if (itemMatches || filteredChildren.length > 0) {
            return { ...item, children: itemMatches ? item.children : filteredChildren };
        }
        return null;
    }).filter((item) => item !== null);
    return filteredData && filteredData.length > 0 ? { ...filter, data: filteredData } : null;
};
const applySearchToFilter = (filter, searchValue) => {
    if (searchValue === "" || filter.label.toLowerCase().includes(searchValue)) {
        return filter;
    }
    if (filter.type === "multi-select") {
        const filtered = filterMultiSelectBySearch(filter, searchValue);
        return filtered.values.length > 0 ? filtered : null;
    }
    if (filter.type === "picklist") {
        return filterPicklistBySearch(filter, searchValue);
    }
    return null;
};
const hasValidData = (filter) => {
    if (filter.type === "multi-select") {
        return filter.values.length > 0;
    }
    if (filter.type === "picklist") {
        return Boolean(filter.data && filter.data.length > 0);
    }
    return true;
};
const expandAllFilters = (filters) => {
    filters.forEach((f) => {
        f.isExpanded = true;
        f.data.forEach((d) => {
            d.isExpanded = true;
            if (d.type === "multi-select") {
                d.values.forEach((v) => {
                    v.isExpanded = true;
                });
            }
        });
    });
};
export const searchFilters = (searchValue, _filters) => {
    let filters = _filters.map((f) => ({
        ...f,
        data: f.data.map((d) => applySearchToFilter(d, searchValue.toLowerCase())).filter((d) => d !== null)
    }));
    filters = filters.map((f) => ({ ...f, data: f.data.filter(hasValidData) }));
    filters = filters.filter((f) => f.data.length > 0);
    expandAllFilters(filters);
    return filters;
};
const processMultiSlider = (newData, detail) => {
    const fixedDecimal = detail.eventData?.fixedDecimal ?? 2;
    const startValue = roundToDecimals(newData.rangeStartValue, fixedDecimal);
    const endValue = roundToDecimals(newData.value, fixedDecimal);
    return {
        fixedDecimal,
        lowerLimit: startValue,
        maxValue: detail.eventData?.maxValue,
        minValue: detail.eventData?.minValue,
        step: detail.eventData?.step,
        type: "number-range",
        upperLimit: endValue
    };
};
const processDate = (newData) => {
    if (!newData.startDateValue || !newData.endDateValue) {
        return undefined;
    }
    const start = tryParseUtcDate(newData.startDateValue, AuxDatePickerDateFormatEnum.DD_MM_YYYY).toDate();
    const end = tryParseUtcDate(newData.endDateValue, AuxDatePickerDateFormatEnum.DD_MM_YYYY).toDate();
    if (start > end) {
        return undefined;
    }
    return {
        endDate: end,
        startDate: start,
        type: "date"
    };
};
const processMultiselect = (newData, oldValues) => {
    const oldStringValues = oldValues && Array.isArray(oldValues) ? oldValues : [];
    const valuesToremove = newData.filter((b) => !b.checked).map((y) => y.value)
        .flat();
    const newValues = oldStringValues.filter((v) => !valuesToremove.includes(v));
    const valuesToAdd = newData.filter((b) => b.checked && !newValues.includes(b.label)).map((y) => y.value)
        .flat();
    newValues.push(...valuesToAdd);
    return newValues;
};
const getValuesToRemoveFromPicklist = (newData) => newData.flatMap((b) => {
    const toRemove = [];
    if (!b.isChecked) {
        toRemove.push(b.uid);
    }
    if (b.children && Array.isArray(b.children)) {
        b.children.forEach((c) => {
            if (!c.isChecked) {
                toRemove.push(`${b.uid};${c.uid}`);
            }
        });
    }
    return toRemove;
});
const getValuesToAddFromPicklist = (newData, newValues) => newData.flatMap((b) => {
    const toAdd = [];
    if (b.isChecked && !newValues.includes(b.uid)) {
        toAdd.push(b.uid);
    }
    if (b.children && Array.isArray(b.children)) {
        b.children.forEach((c) => {
            const childKey = `${b.uid};${c.uid}`;
            if (c.isChecked && !newValues.includes(childKey)) {
                toAdd.push(childKey);
            }
        });
    }
    return toAdd;
});
const processPicklist = (newData, oldValues) => {
    const oldStringValues = oldValues && Array.isArray(oldValues) ? oldValues : [];
    const valuesToremove = getValuesToRemoveFromPicklist(newData);
    const newValues = oldStringValues.filter((v) => !valuesToremove.includes(v));
    const valuesToAdd = getValuesToAddFromPicklist(newData, newValues);
    newValues.push(...valuesToAdd);
    return newValues;
};
export const getChangedFacedFilterOptionValue = (detail, filterValues) => {
    const eventData = detail?.eventData;
    const newData = detail?.newData;
    const oldValues = filterValues[eventData?.field];
    switch (eventData?.type) {
        case AuxFacetedFilterOptionTypeEnum.MultiSlider:
            return processMultiSlider(newData, detail);
        case AuxFacetedFilterOptionTypeEnum.DateRange:
            return processDate(newData);
        case AuxFacetedFilterOptionTypeEnum.MultiSelect:
            return processMultiselect(newData, oldValues);
        case AuxFacetedFilterOptionTypeEnum.PickList:
            return processPicklist(newData, oldValues);
        default:
            return [];
    }
};
export const clearMultiSlider = (filterValue) => {
    const startValue = filterValue.minValue;
    const endValue = filterValue.maxValue;
    return {
        fixedDecimal: filterValue.fixedDecimal,
        lowerLimit: startValue,
        maxValue: filterValue.maxValue,
        minValue: filterValue.minValue,
        step: filterValue.step,
        type: "number-range",
        upperLimit: endValue
    };
};
